import { getSubunitMultiplier } from '@bbpos/utils';
import { getProductTaxRates, getTaxRegime, type CurrencyCode } from '@bbpos/types';
/**
 * Purchase-order line arithmetic — mirror of
 * `services/api/src/purchase-orders/po-line-totals.ts`. Keep the two in
 * lockstep so the wizard preview matches what the API stores.
 *
 * All money is integer minor units (paise). Tax rounds per line by the
 * tenant's regime (API `roundLineTax`): India floors, GCC VAT rounds half-up
 * in minor units. The regime is the tenant's, not the PO currency's.
 *
 *   line_subtotal = floor(quantity × unit_cost)
 *   line_discount = PERCENTAGE ? floor(subtotal × bp / 10000) : min(flat, subtotal)
 *   line_tax      = roundLineTax((subtotal − discount) × gst_rate / 100)
 *   line_total    = subtotal − discount + tax
 */
import type { PoDiscountType } from './types';

export interface PoLineInput {
  quantity: number;
  /** minor units */
  unit_cost: number;
  /** GST % (0–100) */
  gst_rate?: number;
  discount_type?: PoDiscountType;
  /** basis points for PERCENTAGE (1000 = 10%), minor units for FLAT */
  discount_value?: number;
}

export interface PoLineTotals {
  line_subtotal: number;
  line_discount: number;
  line_tax: number;
  line_total: number;
}

/** `tenantCurrency` picks the tax regime; omitted = INR (floor). */
export function computePoLine(input: PoLineInput, tenantCurrency: string = 'INR'): PoLineTotals {
  const lineSubtotal = Math.floor(input.quantity * input.unit_cost);
  const discountValue = Math.max(0, input.discount_value ?? 0);
  const rawDiscount =
    input.discount_type === 'PERCENTAGE'
      ? Math.floor((lineSubtotal * discountValue) / 10000)
      : discountValue;
  const lineDiscount = Math.min(rawDiscount, lineSubtotal);
  const gstRate = Math.max(0, input.gst_rate ?? 0);
  const rawTax = ((lineSubtotal - lineDiscount) * gstRate) / 100;
  // Half-up directly in minor units — never via major units (float half-down).
  const lineTax =
    getTaxRegime(tenantCurrency as CurrencyCode) === 'GST_INDIA'
      ? Math.floor(rawTax)
      : Math.floor(rawTax + 0.5);
  return {
    line_subtotal: lineSubtotal,
    line_discount: lineDiscount,
    line_tax: lineTax,
    line_total: lineSubtotal - lineDiscount + lineTax,
  };
}

/**
 * Rates offered in a purchase line's tax picker (PO wizard, amend, RFQ quote,
 * vendor bill): the tenant regime's rates (`getProductTaxRates`, QA PU2-002) —
 * India's GST slabs, or 0 and the standard VAT rate in the GCC. `current` keeps
 * a saved rate the regime no longer offers visible on an edit form.
 */
export function taxRateOptions(
  tenantCurrency: string,
  current?: number | string | null,
): { value: string; label: string }[] {
  const values = getProductTaxRates(tenantCurrency as CurrencyCode).map(String);
  if (current != null && current !== '' && !values.includes(String(current))) values.push(String(current));
  return values.map((value) => ({ value, label: `${value}%` }));
}

/**
 * Convert the wizard's raw string inputs (major units / percent) to the integer
 * shape the API accepts.
 *
 * `discount_value` carries two different things depending on `discount_type`,
 * and they scale differently: a PERCENTAGE becomes basis points, which is ×100
 * in every currency, while a FLAT amount becomes minor units, which is ×100 only
 * where the currency has two decimals. They used to share one hardcoded ×100,
 * so making it currency-aware without splitting them would have turned every
 * percentage discount into a tenth of itself on OMR/BHD/KWD.
 */
export function toPoLineInput(
  row: {
    quantity: string;
    unit_cost: string;
    gst_rate: string;
    discount_type: PoDiscountType;
    discount_value: string;
  },
  /** The PO's currency: a tenant currency or a supplier's (any purchase currency). */
  currency: string = 'INR',
): PoLineInput {
  const discount = parseFloat(row.discount_value) || 0;
  const subunit = getSubunitMultiplier(currency);
  return {
    quantity: parseFloat(row.quantity) || 0,
    unit_cost: Math.round((parseFloat(row.unit_cost) || 0) * subunit),
    gst_rate: parseInt(row.gst_rate, 10) || 0,
    discount_type: row.discount_type,
    discount_value:
      row.discount_type === 'PERCENTAGE'
        ? Math.round(discount * 100) // not-money: percent → basis points, currency-independent
        : Math.round(discount * subunit), // major → minor units
  };
}
